/**
 * App-local ProtectedRoute — the v3 SDK shipped `MohsinCyberFortProtectedRoute`
 * out of the box; v4 is deliberately minimal and expects apps to own their
 * route protection logic. This component mirrors the v3 surface that the
 * exam app depended on.
 *
 * Behaviour:
 *   - If the SDK is still checking the session (`isLoading`), render nothing
 *     (parent can wrap us in a <Suspense> or show its own skeleton).
 *   - If the user is not authenticated, render `unauthenticatedComponent`
 *     (typically a redirect to Nexus Auth).
 *   - If `roles` is set and the user does not have any of them, render
 *     `unauthorizedComponent`.
 *   - Otherwise, render `children`.
 *
 * This replaces the v3 SDK's `MohsinCyberFortProtectedRoute`. It uses
 * `useExamAuth()` so that role checks combine service-scoped and global
 * roles the same way the v3 hook did.
 */
import { ReactNode } from 'react';
import { useExamAuth } from '@/hooks/useExamAuth';

export interface ProtectedRouteProps {
  children: ReactNode;
  /** If empty, just requires authentication. */
  roles?: string[];
  /** Rendered when the user is unauthenticated. */
  unauthenticatedComponent?: ReactNode;
  /** Rendered when the user is authenticated but lacks any of `roles`. */
  unauthorizedComponent?: ReactNode;
}

export function ProtectedRoute({
  children,
  roles = [],
  unauthenticatedComponent = null,
  unauthorizedComponent = null,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasAnyRole } = useExamAuth();

  // Still bootstrapping the session (initial refresh in flight). Render
  // nothing so the loading UI the caller already mounted (LoadingScreen,
  // dashboard shell, etc.) stays on-screen.
  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <>{unauthenticatedComponent}</>;
  }

  if (roles.length > 0 && !hasAnyRole(roles)) {
    return <>{unauthorizedComponent}</>;
  }

  return <>{children}</>;
}
